import type { ReactNode } from "react";
import {
  CalendarCheck, Check, ChevronDown, ChevronRight, ClipboardList, Headset,
  LockKeyhole, MessageSquare, Phone, ShieldCheck, UserRound,
} from "lucide-react";
import brandMark from "../../assets/clawease-logo-mark-vector.svg";
import whatsAppMark from "../../assets/flow/channel-whatsapp-official.svg";

export type ProductPreviewKind = "chat" | "workflow" | "security" | "control";

function Message({ incoming = false, human = false, children }: { incoming?: boolean; human?: boolean; children: ReactNode }) {
  return (
    <div className={`ce-product-message ${incoming ? "is-incoming" : "is-outgoing"}${human ? " is-human" : ""}`}>
      <span className="ce-product-avatar" aria-hidden="true">
        {incoming ? <UserRound size={13} /> : human ? <Headset size={13} /> : <img src={brandMark} alt="" />}
      </span>
      <p>{human && <span className="ce-product-sender">人工接待</span>}{children}</p>
    </div>
  );
}

function PreviewContent({ kind }: { kind: ProductPreviewKind }) {
  if (kind === "chat") {
    return (
      <div className="ce-product-messages" lang="yue-Hant-HK">
        <Message incoming>唔該，想問下你哋星期六開到幾點？</Message>
        <Message>星期六開到下晝六點呀。</Message>
        <Message incoming>咁我五點半左右到，得唔得？</Message>
        <Message>得呀，冇問題，到時見！</Message>
      </div>
    );
  }
  if (kind === "workflow") {
    return (
      <>
        <Message incoming>想約聽日下晝三點，仲有冇位？</Message>
        <div className="ce-product-execution">
          <div className="ce-product-execution-heading"><ChevronDown size={13} aria-hidden="true" /><strong>預約處理</strong><span>已完成</span></div>
          <ol>
            <li><Check size={12} aria-hidden="true" /><span>理解客人需要</span><small>到店諮詢</small></li>
            <li><Check size={12} aria-hidden="true" /><span>查閱可約時段</span><small>明日 15:00</small></li>
            <li><Check size={12} aria-hidden="true" /><span>建立預約記錄</span><small>已同步</small></li>
          </ol>
        </div>
        <Message>有位呀，已幫你確認預約，到時見！</Message>
      </>
    );
  }
  if (kind === "security") {
    return (
      <>
        <Message incoming>幫我預約下晝三點，唔該。</Message>
        <Message>好呀，已經幫你完成預約。</Message>
        <div className="ce-product-security">
          <div className="ce-product-security-booking">
            <CalendarCheck size={18} aria-hidden="true" />
            <strong>已為陳小姐完成預約</strong>
          </div>
          <ul aria-label="安全保障">
            <li>
              <span className="ce-product-security-icon"><ShieldCheck size={18} aria-hidden="true" /></span>
              <strong>HTTPS 傳輸</strong>
            </li>
            <li>
              <span className="ce-product-security-icon"><LockKeyhole size={18} aria-hidden="true" /></span>
              <strong>敏感資訊加密</strong>
            </li>
            <li>
              <span className="ce-product-security-icon"><ClipboardList size={18} aria-hidden="true" /></span>
              <strong>執行可追蹤</strong>
            </li>
          </ul>
        </div>
      </>
    );
  }
  if (kind === "control") {
    return (
      <>
        <Message incoming>我想申請退款，可唔可以幫我處理？</Message>
        <Message>退款需要由專人處理，我幫你轉接。</Message>
        <div className="ce-product-handoff-status">
          <Headset size={12} aria-hidden="true" />已轉人工
        </div>
        <Message human>你好，我幫你跟進退款。可唔可以提供訂單編號？</Message>
      </>
    );
  }
  return null;
}

/** A simplified, anonymous example based on the real Conversations layout. */
export function HomeProductPreview({ kind }: { kind: ProductPreviewKind }) {
  return (
    <div className="ce-product-stage">
      <div className="ce-product-screen" role="group" aria-label="ClawEase 接待介面示意">
        <div className="ce-product-toolbar">
          <MessageSquare size={17} aria-hidden="true" /><strong>Conversations</strong>
        </div>
        <div className="ce-product-workspace">
          <div className="ce-product-inbox" aria-hidden="true">
            <div className="ce-product-filter">All Channels<ChevronDown size={12} /></div>
            <div className="ce-product-thread is-selected">
              <div><strong>陳小姐</strong><small>15:00</small></div>
              <span className="ce-product-channel"><img src={whatsAppMark} alt="" />WhatsApp</span>
              <p>{kind === "chat" ? "營業時間查詢" : kind === "security" ? "對話記錄" : kind === "control" ? "退款查詢" : "到店預約查詢"}</p>
            </div>
            <div className="ce-product-thread">
              <div><strong>來電查詢</strong><small>11:30</small></div>
              <span className="ce-product-channel is-phone"><Phone size={10} />電話</span>
              <p>已記錄客人需要</p>
            </div>
            <div className="ce-product-inbox-placeholder"><i /><i /><i /></div>
          </div>
          <div className="ce-product-conversation">
            <div className="ce-product-conversation-meta">
              <span className="ce-product-channel"><img src={whatsAppMark} alt="" />WhatsApp</span>
              <span>陳小姐</span>
              {kind === "workflow" && <><ChevronRight size={11} aria-hidden="true" /><span>到店預約</span></>}
            </div>
            <div className="ce-product-conversation-body"><PreviewContent kind={kind} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}
